import { useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useTranslation, Trans } from "react-i18next";
import { Layout } from "@components/Layout";
import { NewsItem } from "@components/NewsItem/NewsItem";
import { FeaturedNewsItem } from "@components/NewsItem/FeaturedNewsItem";
import { Pagination } from "@components/Pagination";
import { Loader } from "@components/Loader";
import { useUserContext } from "@hooks/useUserContext";
import { useOktaUser } from "@hooks/useOktaUser";
import { useNews } from "@hooks/useNews";
import { useUpdatePreference } from "@hooks/useUpdatePreference";
import { homepageNewsItemsCount } from "../constants";
import { NewsStory } from "../types/NewsTypes";
import { getPersonalizedGreeting } from "../utils/getGreeting";
import { languageDutch, languageEnglish } from "../i18n";
import {
  getSlugInLocale,
  getStoryNameInLocale,
} from "../utils/getDataInLocale";
import { sortStoriesWithHeadlines } from "../utils/sortStoriesWithHeadlines";
import { ClusterFilter } from "@components/ClusterFilter";
import { StatusMessage } from "@components/StatusMessage";
import { SearchContainer } from "@components/SearchContainer";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { usePreservedSearchQuery } from "@hooks/usePreservedSearchQuery";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { useResolvedTheme } from "@hooks/useResolvedTheme";
import { getPlaceholderImage } from "@utils/getPlaceholderImage";
import { useEditorPreviewFallback } from "@hooks/useEditorPreviewFallback";
import { useStoryblokStoryNames } from "@hooks/useStoryblokStoryNames";

export const StartPage = () => {
  const { user } = useUserContext();
  const { data: oktaUserData } = useOktaUser();
  const { mutate: updatePreferences } = useUpdatePreference();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { linkState: preservedLinkState } = usePreservedSearchQuery();
  const currentLang = useCurrentLang();

  // Use hook instead of user preference to make sure we get the correct theme when it's set to "system"
  const resolvedTheme = useResolvedTheme();

  const brandParam = searchParams.get("brand");
  const categoryParam = searchParams.get("category");
  const { map: filterNames } = useStoryblokStoryNames([
    brandParam,
    categoryParam,
  ]);

  const clearFilterParam = (key: "brand" | "category") => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next);
  };

  type ActiveFilter = {
    key: "brand" | "category";
    name: string;
    i18nKey: string;
  };
  const activeFilters: ActiveFilter[] = [
    {
      key: "brand" as const,
      name: brandParam ? filterNames.get(brandParam) : undefined,
      i18nKey: "StartPage.filteredByBrand",
    },
    {
      key: "category" as const,
      name: categoryParam ? filterNames.get(categoryParam) : undefined,
      i18nKey: "StartPage.filteredByCategory",
    },
  ].filter((filter): filter is ActiveFilter => !!filter.name);

  // Default the cluster filter to "All" while a brand/category filter is active and the
  // user hasn't touched it yet, instead of compounding it with the stored cluster preference.
  // Once the user explicitly picks a cluster, that choice wins immediately.
  const [hasManuallySelectedCluster, setHasManuallySelectedCluster] =
    useState(false);
  const selectedCluster =
    !hasManuallySelectedCluster && (brandParam || categoryParam)
      ? []
      : user?.filterPreference || [];

  // Redirect to 404 if the language is not supported.
  if (lang && lang !== languageEnglish && lang !== languageDutch) {
    void navigate("/404");
  }

  const [page, setPage] = useState(
    searchParams.get("p") ? Number(searchParams.get("p")) : 1
  );

  // Update the cluster filter preference
  const handleSelectedClusterChange = (cluster: string[]) => {
    setHasManuallySelectedCluster(true);
    if (user) {
      updatePreferences({ filterPreference: cluster });
    }
  };

  const { data, isLoading, error } = useNews({
    content_type: "NewsArticle",
    page: page,
    per_page: homepageNewsItemsCount,
    resolve_relations: "brand,categories,cluster",
    language: currentLang === languageEnglish ? languageEnglish : undefined,
    filter_query: {
      storyTranslated: {
        is: currentLang === languageEnglish ? true : null,
      },
      cluster: {
        in: selectedCluster.length > 0 ? selectedCluster.toString() : undefined,
      },
      brand: { in: brandParam || undefined },
      categories: { in: categoryParam || undefined },
    },
    sort_by: "content.date:desc",
  });

  const allStories = data?.storyData || [];
  const stories = sortStoriesWithHeadlines(allStories);

  const storyHeaders = data?.headers;

  const hasStories = !isLoading && !error && stories && stories?.length > 0;
  const getGridClasses = (index: number) => {
    if (index === 0)
      return "col-span-6 md:col-span-3 xl:col-span-4 mb-6 md:mb-0";
    if (index === 1)
      return "col-span-3 xl:col-span-2 mr-0 mx-4 md:mx-0 mb-3 md:mb-0";
    if (index === 2)
      return "col-span-3 md:col-span-2 xl:col-span-2 ml-0 mx-4 md:mx-0 mb-3 md:mb-0";
    return "col-span-6 md:col-span-2 mx-4 md:mx-0";
  };

  const renderNewsItem = (story: NewsStory, index: number) => {
    const commonProps = {
      title: getStoryNameInLocale(story, currentLang),
      slug: getSlugInLocale(story, currentLang),
      date: story.content?.date,
      imageUrl:
        story.content?.featuredImage?.filename ||
        getPlaceholderImage(resolvedTheme),
      categories: story.content?.categories,
      cluster: story.content?.cluster?.[0]?.name,
      brand: story.content?.brand,
      // Treat a story as leadership when it's explicitly marked as such, or when it's located in the "news/leadership/" folder.
      isLeadership:
        story.content?.isLeadership === true ||
        (story.default_full_slug?.startsWith("news/leadership/") ?? false),
      linkState: preservedLinkState,
      introduction: story.content?.introduction,
      body: story.content?.body,
    };

    if (index === 0) {
      return <FeaturedNewsItem {...commonProps} />;
    }

    if (index === 1) {
      return (
        <>
          <FeaturedNewsItem {...commonProps} className="hidden md:flex" />
          <NewsItem
            {...commonProps}
            imageAlt={story.content?.featuredImage?.alt ?? ""}
            className="md:hidden"
          />
        </>
      );
    }

    return (
      <NewsItem
        {...commonProps}
        imageAlt={story.content?.featuredImage?.alt ?? ""}
        isHorizontal={index > 2}
      />
    );
  };

  // Inside the Visual Editor, a rejected draft would otherwise show a bare inline error inside
  // the partial page chrome. Show a clear, in-place preview-unavailable message instead.
  const previewFallback = useEditorPreviewFallback(!isLoading && !!error);
  if (previewFallback) return previewFallback;

  return (
    <Layout
      translatedSlug={
        currentLang === languageDutch ? `/${languageEnglish}/` : "/"
      }
      className="mb-24"
    >
      <title>{t("StartPage.title")}</title>
      <div className="bg-secondary-900 dark:bg-secondary-950">
        <Typography
          data-hj-suppress
          className="px-4 pt-10 pb-8 text-center text-3xl font-semibold text-white lg:pt-6 lg:text-4xl"
        >
          {getPersonalizedGreeting(t, oktaUserData?.firstName)}
        </Typography>
        <div className="relative mx-auto h-14 w-full max-w-5xl">
          <SearchContainer />
        </div>
      </div>
      <ClusterFilter
        selectedCluster={selectedCluster}
        setSelectedCluster={handleSelectedClusterChange}
      />
      <div className="bg-secondary-900 pb-4 dark:bg-secondary-950">
        {activeFilters.map(({ key, name, i18nKey }) => (
          <div key={key} className={twMerge("mx-auto max-w-lg px-4 2xl:px-0")}>
            <StatusMessage
              variant="info"
              className="text-white"
              pillMessage={t("StartPage.filteredPill")}
              message={
                <Trans
                  i18nKey={i18nKey}
                  values={{ name }}
                  components={{
                    1: (
                      <button
                        type="button"
                        onClick={() => clearFilterParam(key)}
                        className="cursor-pointer text-sm font-semibold underline"
                      >
                        {t("StartPage.clearFilter")}
                      </button>
                    ),
                  }}
                />
              }
            />
          </div>
        ))}
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader />
        </div>
      )}
      {error && <p>{t("StartPage.error")}</p>}
      {!isLoading && !error && !hasStories && (
        <div className="mx-auto mt-16 max-w-lg px-4 2xl:px-0">
          <StatusMessage
            variant="warning"
            pillMessage={t("StartPage.statusMessage")}
            message={
              <Trans
                i18nKey="StartPage.noResultsWithFilters"
                components={{
                  1: (
                    <button
                      type="button"
                      onClick={() => handleSelectedClusterChange([])}
                      className="cursor-pointer text-sm font-semibold underline"
                    >
                      {t("StartPage.clearFilters")}
                    </button>
                  ),
                }}
              />
            }
          />
        </div>
      )}
      {hasStories && (
        <div
          className={twMerge(
            "mx-auto max-w-360 md:px-4 2xl:px-0",
            "before:absolute before:right-0 before:left-0 before:block before:h-60 before:bg-secondary-900 before:content-[''] dark:before:bg-secondary-950"
          )}
        >
          <div className="grid grid-cols-6 gap-x-4 md:gap-6 lg:gap-9">
            {stories.map((story, index) => (
              <div
                key={story.uuid}
                className={`${getGridClasses(index)} ${index > 2 ? "border-b border-tertiary-100 py-3 last-of-type:border-none md:border-none md:py-0 dark:border-white/20" : ""}`}
              >
                {renderNewsItem(story, index)}
              </div>
            ))}
          </div>
          {storyHeaders?.total && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(
                Number(storyHeaders.total) / homepageNewsItemsCount
              )}
              setPage={setPage}
              pathname={location.pathname}
            />
          )}
        </div>
      )}
    </Layout>
  );
};
